import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { ThemeConfig } from '../components/admin/appearance/ThemeEditor';
import { generatePaletteFromBase, ColorPalette } from '../utils/colorGeneration';
import type { DefaultThemeId } from './defaultThemeManagementService';
import {
  colorSchemes,
  colorSchemesPastel,
  colorSchemesEarth,
  colorSchemesSage,
  colorSchemesOcean,
  colorSchemesWhite,
  colorSchemesBlack,
  colorSchemesSunset,
  colorSchemesForest,
  colorSchemesMidnight,
  colorSchemesAurora,
  colorSchemesAmber,
  colorSchemesEmerald,
  colorSchemesMuiOfficial,
  colorSchemesSlateBluePlus,
} from '../theme/themePrimitives';

export interface CustomTheme extends ThemeConfig {
  id: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
  isDefault?: boolean;
}

const THEMES_COLLECTION = 'customThemes';

/**
 * Get all custom themes
 */
export const getAllCustomThemes = async (): Promise<CustomTheme[]> => {
  try {
    const themesRef = collection(db, THEMES_COLLECTION);
    const q = query(themesRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as CustomTheme[];
  } catch (error) {
    // Error('Error fetching custom themes:', error);
    throw error;
  }
};

/**
 * Get visible themes only (not hidden)
 * Note: We fetch all themes and filter client-side to avoid needing a composite index
 */
export const getVisibleCustomThemes = async (): Promise<CustomTheme[]> => {
  try {
    const themesRef = collection(db, THEMES_COLLECTION);
    // Fetch all themes and filter client-side to avoid composite index requirement
    const q = query(themesRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    // Filter out hidden themes client-side
    const themes = snapshot.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as CustomTheme[];

    return themes.filter((theme) => !theme.isHidden);
  } catch (error) {
    // Error('Error fetching visible themes:', error);
    throw error;
  }
};

/**
 * Get a single theme by ID
 */
export const getCustomThemeById = async (themeId: string): Promise<CustomTheme | null> => {
  try {
    const themeRef = doc(db, THEMES_COLLECTION, themeId);
    const themeDoc = await getDoc(themeRef);

    if (!themeDoc.exists()) {
      return null;
    }

    return {
      id: themeDoc.id,
      ...themeDoc.data(),
    } as CustomTheme;
  } catch (error) {
    // Error('Error fetching theme:', error);
    throw error;
  }
};

/**
 * Create a new custom theme
 */
export const createCustomTheme = async (
  theme: ThemeConfig,
  userId: string
): Promise<string> => {
  try {
    const themesRef = collection(db, THEMES_COLLECTION);
    const newThemeRef = doc(themesRef);

    // Destructurer pour exclure explicitement le champ 'id'
    const { id, ...themeWithoutId } = theme;

    // Nettoyer les données avant sauvegarde : retirer les champs undefined et null
    const cleanedTheme: any = {};
    Object.entries(themeWithoutId).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Pour les objets vides, ne pas les inclure
        if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
          return;
        }
        cleanedTheme[key] = value;
      }
    });

    const themeData = {
      ...cleanedTheme,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId,
      isDefault: false,
    };

    // Vérification finale : s'assurer qu'il n'y a pas de champ 'id'
    if ('id' in themeData) {
      delete themeData.id;
    }

    await setDoc(newThemeRef, themeData);

    return newThemeRef.id;
  } catch (error) {
    // Error('Error creating theme:', error);
    throw error;
  }
};

/**
 * Update an existing custom theme
 */
export const updateCustomTheme = async (
  themeId: string,
  updates: Partial<ThemeConfig>
): Promise<void> => {
  try {
    const themeRef = doc(db, THEMES_COLLECTION, themeId);

    // Destructurer pour exclure explicitement le champ 'id'
    const { id, ...updatesWithoutId } = updates;

    // Nettoyer les données avant sauvegarde : retirer les champs undefined et null
    const cleanedUpdates: any = {};
    Object.entries(updatesWithoutId).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Pour les objets vides, ne pas les inclure
        if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
          return;
        }
        cleanedUpdates[key] = value;
      }
    });

    // Vérification finale : s'assurer qu'il n'y a pas de champ 'id'
    if ('id' in cleanedUpdates) {
      delete cleanedUpdates.id;
    }

    await updateDoc(themeRef, {
      ...cleanedUpdates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    // Error('Error updating theme:', error);
    throw error;
  }
};

/**
 * Delete a custom theme
 */
export const deleteCustomTheme = async (themeId: string): Promise<void> => {
  try {
    const themeRef = doc(db, THEMES_COLLECTION, themeId);
    await deleteDoc(themeRef);
  } catch (error) {
    // Error('Error deleting theme:', error);
    throw error;
  }
};

/**
 * Toggle theme visibility (hide/show)
 */
export const toggleThemeVisibility = async (
  themeId: string,
  isHidden: boolean
): Promise<void> => {
  try {
    const themeRef = doc(db, THEMES_COLLECTION, themeId);

    await updateDoc(themeRef, {
      isHidden,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    // Error('Error toggling theme visibility:', error);
    throw error;
  }
};

/**
 * Set a theme as default
 */
export const setDefaultTheme = async (themeId: string): Promise<void> => {
  try {
    // First, unset all themes as default
    const themesRef = collection(db, THEMES_COLLECTION);
    const allThemes = await getDocs(themesRef);

    const updatePromises = allThemes.docs.map((themeDoc) =>
      updateDoc(themeDoc.ref, { isDefault: false })
    );

    await Promise.all(updatePromises);

    // Then set the selected theme as default
    const themeRef = doc(db, THEMES_COLLECTION, themeId);
    await updateDoc(themeRef, {
      isDefault: true,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    // Error('Error setting default theme:', error);
    throw error;
  }
};

/**
 * Get the default theme
 */
export const getDefaultTheme = async (): Promise<CustomTheme | null> => {
  try {
    const themesRef = collection(db, THEMES_COLLECTION);
    const q = query(themesRef, where('isDefault', '==', true));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const themeDoc = snapshot.docs[0];
    return {
      id: themeDoc.id,
      ...themeDoc.data(),
    } as CustomTheme;
  } catch (error) {
    // Error('Error fetching default theme:', error);
    throw error;
  }
};

/**
 * Duplicate a theme
 */
export const duplicateTheme = async (
  themeId: string,
  userId: string,
  newName: string
): Promise<string> => {
  try {
    const originalTheme = await getCustomThemeById(themeId);

    if (!originalTheme) {
      throw new Error('Theme not found');
    }

    const { id, createdAt, updatedAt, createdBy, ...themeData } = originalTheme;

    return await createCustomTheme(
      {
        ...themeData,
        name: newName,
      },
      userId
    );
  } catch (error) {
    // Error('Error duplicating theme:', error);
    throw error;
  }
};

/**
 * Get color scheme for a default theme ID
 */
const getColorSchemeForTheme = (themeId: DefaultThemeId) => {
  switch (themeId) {
    case 'standard':
      return colorSchemes;
    case 'pastel':
      return colorSchemesPastel;
    case 'earth':
      return colorSchemesEarth;
    case 'sage':
      return colorSchemesSage;
    case 'ocean':
      return colorSchemesOcean;
    case 'white':
      return colorSchemesWhite;
    case 'black':
      return colorSchemesBlack;
    case 'sunset':
      return colorSchemesSunset;
    case 'forest':
      return colorSchemesForest;
    case 'midnight':
      return colorSchemesMidnight;
    case 'aurora':
      return colorSchemesAurora;
    case 'amber':
      return colorSchemesAmber;
    case 'emerald':
      return colorSchemesEmerald;
    case 'slate-blue':
      return colorSchemesSlateBluePlus;
    case 'mui-official':
      return colorSchemesMuiOfficial;
    case 'glass':
      // Glass theme uses standard color scheme but with special styling
      return colorSchemes;
    default:
      return colorSchemes;
  }
};

/**
 * Extract base color from a color scheme
 */
const extractBaseColor = (colorScheme: any): string => {
  // Use primary.main from light mode as base color
  const primaryMain = colorScheme.light?.palette?.primary?.main;
  if (primaryMain) {
    return primaryMain;
  }
  // Fallback to a default blue
  return '#3498db';
};

/**
 * Convert a default theme to a custom theme
 * This creates a new custom theme based on the default theme's color scheme
 */
export const convertDefaultThemeToCustom = async (
  themeId: DefaultThemeId,
  userId: string,
  customName?: string
): Promise<string> => {
  try {
    // Get the color scheme for this default theme
    const colorScheme = getColorSchemeForTheme(themeId);

    // Extract base color from primary.main
    const baseColor = extractBaseColor(colorScheme);

    // Generate palette from base color
    // Use default offset and whiteBalance (0, 0) to get a standard palette
    const palette: ColorPalette = generatePaletteFromBase(baseColor, 0, 0);

    // Get theme name from default theme names
    const defaultThemeNames: Record<DefaultThemeId, string> = {
      standard: 'Standard',
      glass: 'Glass',
      pastel: 'Pastel',
      earth: 'Earth',
      sage: 'Sage',
      ocean: 'Ocean',
      white: 'White',
      black: 'Black',
      sunset: 'Sunset',
      forest: 'Forest',
      midnight: 'Midnight',
      aurora: 'Aurora',
      amber: 'Amber',
      emerald: 'Emerald',
      'slate-blue': 'Slate Blue+',
      'mui-official': 'MUI Official',
    };

    const themeName = customName || `${defaultThemeNames[themeId]} (Personnalisé)`;

    // Create custom theme config
    const themeConfig: ThemeConfig = {
      name: themeName,
      baseColor,
      offset: 0,
      whiteBalance: 0,
      palette,
      isHidden: false,
    };

    // Create the custom theme
    return await createCustomTheme(themeConfig, userId);
  } catch (error) {
    // Error('Error converting default theme to custom:', error);
    throw error;
  }
};
